import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserButton } from "@clerk/clerk-react";
import { Check, Loader2 } from "lucide-react";
import { useApi } from "@/lib/api";
import { Wordmark } from "@/components/Brand";

// Seven progress lines. The backend reports four coarse stages (reading,
// mapping, writing, done), so each stage owns a range of lines and the
// screen walks through that range on a timer, never running ahead of the
// real stage. When the job is done the remaining lines tick off quickly
// and the creator lands on their dashboard. There is no review step.
const LINES = [
    "Setting up your profile",
    "Reading your recent posts",
    "Finding what you're known for",
    "Mapping your city and niche",
    "Writing your creator page",
    "Getting you ready to be named",
    "Let's get you named",
];

// Highest line index that may be active while the job is in a stage.
const STAGE_CAP = {
    reading: 1,
    mapping: 3,
    writing: 5,
};

const STEP_MS = 1800;
const FINISH_STEP_MS = 450;
const DONE_PAUSE_MS = 900;

export default function BuildScreen() {
    const { request } = useApi();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    // Index of the currently active line. LINES.length means all done.
    const [active, setActive] = useState(0);
    const timerRef = useRef(null);

    const refresh = useCallback(async () => {
        try {
            const res = await request("/build/status");
            setJob(res?.job || null);
        } catch {
            /* keep going */
        }
    }, [request]);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            await refresh();
            if (!cancelled) setLoading(false);
        };
        run();
        timerRef.current = setInterval(() => {
            if (!cancelled) refresh();
        }, 2000);
        return () => {
            cancelled = true;
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [refresh]);

    const stage = job?.stage;
    const finished = stage === "done";
    const cap = finished ? LINES.length : STAGE_CAP[stage] ?? 0;

    // Walk the active line toward the cap for the current stage.
    useEffect(() => {
        if (active >= cap) return undefined;
        const t = setTimeout(
            () => setActive((a) => Math.min(a + 1, cap)),
            finished ? FINISH_STEP_MS : STEP_MS,
        );
        return () => clearTimeout(t);
    }, [active, cap, finished]);

    // All lines ticked: hand over to the dashboard.
    useEffect(() => {
        if (!finished || active < LINES.length) return undefined;
        const t = setTimeout(
            () => navigate("/dashboard", { replace: true }),
            DONE_PAUSE_MS,
        );
        return () => clearTimeout(t);
    }, [finished, active, navigate]);

    if (loading) {
        return (
            <div className="min-h-screen bg-paper flex items-center justify-center text-sm text-black/50">
                Loading...
            </div>
        );
    }

    if (!job) {
        return (
            <div className="min-h-screen bg-paper">
                <Header />
                <main className="px-5 pt-6 pb-16 sm:px-8 max-w-md mx-auto">
                    <div className="samoony-label mb-3">Nothing to build yet</div>
                    <p className="text-sm text-black/60">
                        Finish onboarding first.
                    </p>
                    <Link
                        to="/onboarding"
                        className="samoony-pill samoony-pill-primary mt-6"
                        data-testid="build-go-onboarding"
                    >
                        Go to onboarding
                    </Link>
                </main>
            </div>
        );
    }

    if (stage === "blocked") return <BuildBlocked />;
    if (stage === "failed") return <BuildFailed />;
    if (stage === "held") return <BuildHeld />;

    return (
        <div className="min-h-screen bg-paper">
            <Header />
            <main className="px-5 pt-6 pb-16 sm:px-8 max-w-md mx-auto">
                <div className="samoony-label mb-3">Building your page</div>
                <h1
                    className="samoony-hero text-ink mb-10"
                    style={{ fontSize: "clamp(60px, 16vw, 120px)", fontStretch: "68%" }}
                >
                    One minute.
                </h1>
                <ol className="space-y-5" data-testid="build-progress">
                    {LINES.map((label, i) => {
                        const isDone = active > i;
                        const isActive = active === i;
                        return (
                            <li
                                key={label}
                                data-testid={`build-line-${i}`}
                                data-state={
                                    isDone ? "done" : isActive ? "active" : "pending"
                                }
                                className="flex items-center gap-4 transition-all duration-500"
                                style={{
                                    opacity: isDone || isActive ? 1 : 0.3,
                                    transform: isDone || isActive
                                        ? "translateX(0)"
                                        : "translateX(6px)",
                                }}
                            >
                                <div className="h-10 w-10 rounded-full flex items-center justify-center border-2 border-ink/15 bg-white shrink-0">
                                    {isDone ? (
                                        <Check size={18} className="text-signal" />
                                    ) : isActive ? (
                                        <Loader2 size={18} className="animate-spin text-ink" />
                                    ) : (
                                        <span className="h-2 w-2 rounded-full bg-ink/15" />
                                    )}
                                </div>
                                <div className="font-display text-base sm:text-lg text-ink">
                                    {label}
                                </div>
                            </li>
                        );
                    })}
                </ol>
            </main>
        </div>
    );
}

function Header() {
    return (
        <header className="flex items-center justify-between px-5 py-4 sm:px-8 sm:py-6 max-w-md mx-auto">
            <Link to="/" className="no-underline">
                <Wordmark height={26} />
            </Link>
            <UserButton afterSignOutUrl="/" />
        </header>
    );
}

function BuildHeld() {
    return (
        <div className="min-h-screen bg-paper">
            <Header />
            <main
                data-testid="build-held"
                className="px-5 pt-6 pb-16 sm:px-8 max-w-md mx-auto animate-fade-up"
            >
                <div className="samoony-label mb-3">Almost there</div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-[1.1] mb-4">
                    We're putting the final touches on your page.
                </h1>
                <p className="text-sm text-black/60">
                    We'll let you know as soon as it's live.
                </p>
            </main>
        </div>
    );
}

function BuildBlocked() {
    return (
        <div className="min-h-screen bg-paper">
            <Header />
            <main
                data-testid="build-blocked"
                className="px-5 pt-6 pb-16 sm:px-8 max-w-md mx-auto animate-fade-up"
            >
                <div className="samoony-label mb-3">Not yet</div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-[1.1] mb-4">
                    Post a few more times and we will finish your page.
                </h1>
                <p className="text-sm text-black/60">Try again tomorrow.</p>
            </main>
        </div>
    );
}

function BuildFailed() {
    return (
        <div className="min-h-screen bg-paper">
            <Header />
            <main
                data-testid="build-failed"
                className="px-5 pt-6 pb-16 sm:px-8 max-w-md mx-auto animate-fade-up"
            >
                <div className="samoony-label mb-3">Something went wrong</div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-[1.1] mb-4">
                    Something went wrong. Try again in a minute.
                </h1>
            </main>
        </div>
    );
}
